import {ITicket, TicketStore} from '@/store/modules/ticket.store';
import moment from 'moment-timezone';
import {ICreateTicket, IUpdateBadge, IUpdateTicket} from '@/TPWebClient';
import {BadgeStore} from '@/store/modules/badge.store';
import {LocationStore} from '@/store/modules/location.store';
import {ILocationDTO} from '@/TPWebClient/dtos/location.dto';
import Axios from 'axios';
import {AuthStore} from '@/store/modules/auth.store';
import TPWebClientInternal from '@/TPWebClientInternal';
import {IMenu, IMenuCategory, MenuStore} from '@/store/modules/menu.store';
import {IUpdateLineItemDTO} from '@/TPWebClientInternal/dtos/update-line-item.dto';
import {IAddLineItemDTO} from '@/TPWebClientInternal/dtos/add-line-item.dto';
import {ICreateItemDTO} from '@/TPWebClientInternal/dtos/create-item.dto';
import {IUpdateItemDTO} from '@/TPWebClientInternal/dtos/update-item.dto';
import {ICreateOptionDTO} from '@/TPWebClientInternal/dtos/create-option.dto';
import {IUpdateOptionDTO} from '@/TPWebClientInternal/dtos/update-option.dto';
import {ICreateChoiceDTO} from '@/TPWebClientInternal/dtos/create-choice.dto';
import {IUpdateChoiceDTO} from '@/TPWebClientInternal/dtos/update-choice.dto';
import {ICreateCategoryDTO} from '@/TPWebClientInternal/dtos/create-category.dto';
import {IUpdateCategoryDTO} from '@/TPWebClientInternal/dtos/update-category.dto';
import {ICreateMenuDTO} from '@/TPWebClientInternal/dtos/create-menu.dto';
import {IUpdateMenuDTO} from '@/TPWebClientInternal/dtos/update-menu.dto';

class Web {
	client: TPWebClientInternal | null = null;
	started!: Promise<void>;
	private startedResolve!: () => void;

	constructor() {
		// this.client = new TPWebClient(
		//  'http://localhost:8081/https://c20.tp-devel.com/api/v1/location/',
		// 	'tpApi_7e8132bbdae4edbf7b7680b5e8af6f6a',
		// 	'pg11dev~d2eee0ab74fdd14e3c3ebcd0a979bc271'
		// );
		this.setupStartedPromise();
	}

	setupStartedPromise() {
		this.started = new Promise<void>((resolve => {
			this.startedResolve = resolve;
		}));
	}

	async testAuth(host: string, locationBase: string, apiKey: string) {
		try {
			const client = new TPWebClientInternal(
				host,
				apiKey,
				locationBase,
				[],
				'none'
			);
			await client.testAuth();
			client.stop();
			return true;
		} catch(e) {
			return false;
		}
	}

	async start(host: string, locationBase: string, apiKey: string) {
		this.client = new TPWebClientInternal(
			host,
			apiKey,
			locationBase,
			['tickets', 'badges'],
			'ws'
		);
		// this.client = new TPWebClientInternal(
		// 	host,
		// 	apiKey,
		// 	locationBase,
		// 	['tickets', 'badges'],
		// 	'poll'
		// );


		this.client.locationUpdated().subscribe((location) => {
			LocationStore.addOrUpdateLocation(location);
		});

		this.client.badgeUpdated().subscribe((badge) => {
			BadgeStore.addOrUpdateBadge(badge);
		});

		this.client.badgeDeleted().subscribe((badgeId) => {
			BadgeStore.removeBadge(badgeId);
		});

		// this.client.ticketsUpdated().subscribe((tickets) => {
		// 	TicketStore.addOrUpdateTicket(tickets);
		// });
		this.client.ticketUpdated().subscribe((ticket) => {
			TicketStore.addOrUpdateTicket(ticket);
		});

		this.client.ticketClosed().subscribe((ticketId) => {
			TicketStore.removeTicket(ticketId);
		});

		await this.client.start();

		// Internal calls
		await this.fetchMenu();

		this.startedResolve();
	}

	stop() {
		if(this.client) {
			this.client.stop();
			this.client = null;
		}
		this.setupStartedPromise();
	}

	async createTicket(updateTicket: ICreateTicket) {
		if(this.client === null) {
			throw new Error('Client not started');
		}
		const response = await this.client.createTicket(updateTicket);
		if(response.data.status !== 200) {
			throw new Error(response.data.status + '');
		} else {
			TicketStore.addOrUpdateTicket(response.data.data.ticket);
		}
		return response.data.data.ticket.ticketId;
	}

	async updateTicket(ticketId: string, updateTicket: IUpdateTicket) {
		if(this.client === null) {
			throw new Error('Client not started');
		}
		const response = await this.client.updateTicket(ticketId, updateTicket);
		if(response.data.status !== 200) {
			throw new Error(response.data.status + '');
		} else {
			TicketStore.addOrUpdateTicket(response.data.data.ticket);
		}
	}

	async seatWaitlist(ticketId: string, badgeId: string) {
		const ticket = TicketStore.byId(ticketId) as ITicket;
		await this.updateTicket(ticket.ticketId, {
			badgeId: badgeId,
			customer: ticket.customer,
			total: 0,
			partySize: ticket.partySize,
			waitTime: null,
			orderNotes: ticket.orderNotes
		});
	}

	async closeTicket(ticketId: string) {
		if(this.client === null) {
			throw new Error('Client not started');
		}
		const response = await this.client.closeTicket(ticketId);
		if(response.data.status !== 200) {
			throw new Error(response.data.status + '');
		} else {
			TicketStore.removeTicket(response.data.data.ticket.ticketId);
		}
	}

	async deletePayment(ticketId: string, paymentId: string) {
		if(this.client === null) {
			throw new Error('Client not started');
		}
		const response = await this.client.deletePayment(ticketId, paymentId);
		if(response.data.status !== 200) {
			throw new Error(response.data.status + '');
		} else {
			return response;
		}
	}

	async sendSms(ticketId: string, phoneNumber: string) {
		if(this.client === null) {
			throw new Error('Client not started');
		}
		const response = await this.client.sendSms(ticketId, phoneNumber);
		if(response.data.status !== 200) {
			throw new Error(response.data.status + '');
		} else {
			return response;
		}
	}

	async searchCustomers(phoneNumber: string) {
		if(this.client === null) {
			throw new Error('Client not started');
		}
		const response = await this.client.searchCustomers(phoneNumber);
		if(response.data.status !== 200) {
			throw new Error(response.data.status + '');
		} else {
			if(response.data.data.customers === null) {
				return [];
			}
			return response.data.data.customers.map((customer: any) => {
				customer.phone = customer.phone + '';
				if(customer.name && customer.name.indexOf(', ') !== -1) {
					customer.name = customer.name.split(', ').reverse().join(' ');
				}
				return customer;
			});
		}
	}

	async tokenExchange(apiHost: string, locationId: string, token: string, pin: string) {
		const localAxios = Axios.create();
		const response = await localAxios.post('https://' + apiHost + '/api/v1/location/' + locationId + '/auth', {
			token: token,
			pin: pin
		});
		AuthStore.setAuthCredentials({
			host: 'https://' + response.data.data.auth.apiHost + (response.data.data.auth.apiHost.endsWith('/') ? '' : '/') + 'api/v1/location/',
			locationBase: response.data.data.auth.locationId,
			apiKey: response.data.data.auth.apiKey
		});
	}

	async createBadge(name: string) {
		if(this.client === null) {
			throw new Error('Client not started');
		}
		const response = await this.client.createBadge(name);
		if(response.data.status !== 200) {
			throw new Error(response.data.status + '');
		} else {
			BadgeStore.addOrUpdateBadge(response.data.data.badge);
		}
		return response.data.data.badge.badgeId;
	}

	async updateBadge(badgeId: string, updateBadge: IUpdateBadge) {
		if(this.client === null) {
			throw new Error('Client not started');
		}
		const response = await this.client.updateBadge(badgeId, updateBadge);
		if(response.data.status !== 200) {
			throw new Error(response.data.status + '');
		} else {
			BadgeStore.addOrUpdateBadge(response.data.data.badge);
		}
	}

	async deleteBadge(badgeId: string) {
		if(this.client === null) {
			throw new Error('Client not started');
		}
		const response = await this.client.deleteBadge(badgeId);
		if(response.data.status !== 200) {
			throw new Error(response.data.status + '');
		} else {
			BadgeStore.removeBadge(badgeId);
		}
	}

	async sendSmsAuth(phoneNumber: string) {
		if(this.client === null) {
			throw new Error('Client not started');
		}
		const response = await this.client.sendSmsAccess(phoneNumber);
		if(response.data.status !== 200) {
			throw new Error(response.data.status + '');
		} else {
			return response;
		}
	}

	async fetchMenu(menuId = 'live') {
		if(this.client === null) {
			throw new Error('Client not started');
		}
		const response = await this.client.fetchMenu(menuId);
		if(response.data.status !== 200) {
			throw new Error(response.data.status + '');
		} else {
			response.data.data.menu.categories.map((category, index) => {
				(category as IMenuCategory).categoryId = index;
				return category;
			});
			if(menuId === 'live') {
				MenuStore.addOrUpdateMenuAndSetActive(response.data.data.menu as IMenu);
			} else {
				MenuStore.addOrUpdateMenu(response.data.data.menu as IMenu);
			}

		}
	}

	async getMenus() {
		if(this.client === null) {
			throw new Error('Client not started');
		}
		const response = await this.client.getMenus();
		if(response.data.status !== 200) {
			throw new Error(response.data.status + '');
		} else {
			Object.values(response.data.data.menus).forEach((menu) => {
				menu.categories.map((category, index) => {
					(category as IMenuCategory).categoryId = index;
					return category;
				});
				MenuStore.addOrUpdateMenu(menu as IMenu);
			});
		}
	}

	async addLineItem(ticketId: string, menuId: string, items: IAddLineItemDTO[]) {
		if(this.client === null) {
			throw new Error('Client not started');
		}
		const response = await this.client.addLineItem(ticketId, menuId, items);
		console.log(response);
		if(response.data.status !== 200) {
			throw new Error(response.data.status + '');
		} else {
			// MenuStore.addOrUpdateMenu(response.data.data.menu as IMenu);
		}
	}

	async updateLineItem(ticketId: string, item: IUpdateLineItemDTO) {
		if(this.client === null) {
			throw new Error('Client not started');
		}
		const response = await this.client.updateLineItem(ticketId, item);
		console.log(response);
		if(response.data.status !== 200) {
			throw new Error(response.data.status + '');
		} else {
			// MenuStore.addOrUpdateMenu(response.data.data.menu as IMenu);
		}
	}

	async deleteLineItem(ticketId: string, lineItemId: number) {
		if(this.client === null) {
			throw new Error('Client not started');
		}
		const response = await this.client.deleteLineItem(ticketId, lineItemId);
		console.log(response);
		if(response.data.status !== 200) {
			throw new Error(response.data.status + '');
		} else {
			// MenuStore.addOrUpdateMenu(response.data.data.menu as IMenu);
		}
	}

	// Menu Editing

	async createItem(body: ICreateItemDTO) {
		if(this.client === null) {
			throw new Error('Client not started');
		}
		const response = await this.client.createItem(body);
		console.log(response);
		if(response.data.status !== 200) {
			throw new Error(response.data.status + '');
		} else {
			// await this.fetchMenu(menuId);
			return response.data.data.item.id;
			// MenuStore.addOrUpdateMenu(response.data.data.menu as IMenu);
		}
	}

	async addItemToMenu(menuId: string, categoryId: number, itemId: string) {
		if(this.client === null) {
			throw new Error('Client not started');
		}
		const response = await this.client.addItemToMenu(menuId, categoryId, itemId);
		console.log(response);
		if(response.data.status !== 200) {
			throw new Error(response.data.status + '');
		} else {
			await this.fetchMenusWithItem(itemId);
			// await this.fetchMenu(menuId);
			return true;
			// MenuStore.addOrUpdateMenu(response.data.data.menu as IMenu);
		}
	}

	// Still requiring the menuId here so we know to re-fetch it
	// if we ever add a "master item edit" I'll rework this to allow for null
	// menuId and we can skip the update step
	async updateItem(menuId: string, body: IUpdateItemDTO) {
		if(this.client === null) {
			throw new Error('Client not started');
		}
		const response = await this.client.updateItem(body);
		console.log(response);
		if(response.data.status !== 200) {
			throw new Error(response.data.status + '');
		} else {
			await this.fetchMenusWithItem(body.itemId);
			// await this.fetchMenu(menuId);
			return response.data.itemId;
		}
	}

	async removeItemFromMenu(menuId: string, categoryId: number, itemId: string) {
		if(this.client === null) {
			throw new Error('Client not started');
		}
		const response = await this.client.removeItemFromMenu(menuId, categoryId, itemId);
		console.log(response);
		if(response.data.status !== 200) {
			throw new Error(response.data.status + '');
		} else {
			await this.fetchMenu(menuId);
			return true;
		}
	}

	async createOption(body: ICreateOptionDTO) {
		if(this.client === null) {
			throw new Error('Client not started');
		}
		const response = await this.client.createOption(body);
		console.log(response);
		if(response.data.status !== 200) {
			throw new Error(response.data.status + '');
		} else {
			// await this.fetchMenu(menuId);
			return response.data.data.option.optionId;
			// MenuStore.addOrUpdateMenu(response.data.data.menu as IMenu);
		}
	}

	// Still requiring the menuId here so we know to re-fetch it
	async addOptionToItem(menuId: string, itemId: string, optionId: string) {
		if(this.client === null) {
			throw new Error('Client not started');
		}
		const response = await this.client.addOptionToItem(itemId, optionId);
		console.log(response);
		if(response.data.status !== 200) {
			throw new Error(response.data.status + '');
		} else {
			await this.fetchMenusWithItem(itemId);
			// await this.fetchMenu(menuId);
			return true;
			// MenuStore.addOrUpdateMenu(response.data.data.menu as IMenu);
		}
	}

	// Still requiring the menuId here so we know to re-fetch it
	// if we ever add a "master option edit" I'll rework this to allow for null
	// menuId and we can skip the update step
	async updateOption(menuId: string, body: IUpdateOptionDTO) {
		if(this.client === null) {
			throw new Error('Client not started');
		}
		const response = await this.client.updateOption(body);
		console.log(response);
		if(response.data.status !== 200) {
			throw new Error(response.data.status + '');
		} else {
			await this.fetchMenusWithOption(body.optionId);
			// await this.fetchMenu(menuId);
			return response.data.itemId;
		}
	}

	async removeOptionFromItem(menuId: string, itemId: string, optionId: string) {
		if(this.client === null) {
			throw new Error('Client not started');
		}
		const response = await this.client.removeOptionFromItem(itemId, optionId);
		console.log(response);
		if(response.data.status !== 200) {
			throw new Error(response.data.status + '');
		} else {
			await this.fetchMenusWithOption(optionId);
			// await this.fetchMenu(menuId);
			return true;
		}
	}

	// Still requiring the menuId here so we know to re-fetch it
	async createChoice(menuId: string, optionId: string, body: ICreateChoiceDTO) {
		if(this.client === null) {
			throw new Error('Client not started');
		}
		const response = await this.client.createChoice(optionId, body);
		console.log(response);
		if(response.data.status !== 200) {
			throw new Error(response.data.status + '');
		} else {
			await this.fetchMenusWithOption(optionId);
			// await this.fetchMenu(menuId);
			return true;
			// MenuStore.addOrUpdateMenu(response.data.data.menu as IMenu);
		}
	}

	// Still requiring the menuId here so we know to re-fetch it
	// if we ever add a "master option edit" I'll rework this to allow for null
	// menuId and we can skip the update step
	async updateChoice(menuId: string, optionId: string, body: IUpdateChoiceDTO) {
		if(this.client === null) {
			throw new Error('Client not started');
		}
		const response = await this.client.updateChoice(optionId, body);
		console.log(response);
		if(response.data.status !== 200) {
			throw new Error(response.data.status + '');
		} else {
			await this.fetchMenusWithOption(optionId);
			// await this.fetchMenu(menuId);
			return response.data.itemId;
		}
	}

	async removeChoiceFromOption(menuId: string, optionId: string, choiceId: string) {
		if(this.client === null) {
			throw new Error('Client not started');
		}
		const response = await this.client.removeChoiceFromOption(optionId, choiceId);
		console.log(response);
		if(response.data.status !== 200) {
			throw new Error(response.data.status + '');
		} else {
			await this.fetchMenusWithOption(optionId);
			// await this.fetchMenu(menuId);
			return true;
		}
	}

	// Just return, we won't store this in Vuex
	async getItems() {
		if(this.client === null) {
			throw new Error('Client not started');
		}
		const response = await this.client.getItems();
		console.log(response);
		if(response.data.status !== 200) {
			throw new Error(response.data.status + '');
		} else {
			return response.data.data.items;
		}
	}

	// Just return, we won't store this in Vuex
	async getOptions() {
		if(this.client === null) {
			throw new Error('Client not started');
		}
		const response = await this.client.getOptions();
		console.log(response);
		if(response.data.status !== 200) {
			throw new Error(response.data.status + '');
		} else {
			return Object.values(response.data.data.options);
		}
	}

	async createCategory(menuId: string, body: ICreateCategoryDTO) {
		if(this.client === null) {
			throw new Error('Client not started');
		}
		const response = await this.client.createCategory(menuId, body);
		console.log(response);
		if(response.data.status !== 200) {
			throw new Error(response.data.status + '');
		} else {
			await this.fetchMenu(menuId);
			return response.data.data.category.categoryId;
		}
	}

	async updateCategory(menuId: string, body: IUpdateCategoryDTO) {
		if(this.client === null) {
			throw new Error('Client not started');
		}
		const response = await this.client.updateCategory(menuId, body);
		console.log(response);
		if(response.data.status !== 200) {
			throw new Error(response.data.status + '');
		} else {
			await this.fetchMenu(menuId);
			return true;
		}
	}

	async deleteCategory(menuId: string, categoryId: string) {
		if(this.client === null) {
			throw new Error('Client not started');
		}
		const response = await this.client.deleteCategory(menuId, categoryId);
		console.log(response);
		if(response.data.status !== 200) {
			throw new Error(response.data.status + '');
		} else {
			await this.fetchMenu(menuId);
			return true;
		}
	}

	async createMenu(body: ICreateMenuDTO) {
		if(this.client === null) {
			throw new Error('Client not started');
		}
		const response = await this.client.createMenu(body);
		console.log(response);
		if(response.data.status !== 200) {
			throw new Error(response.data.status + '');
		} else {
			MenuStore.addOrUpdateMenu(response.data.data.menu);
			return response.data.data.menu.menuId;
		}
	}

	async updateMenu(body: IUpdateMenuDTO) {
		if(this.client === null) {
			throw new Error('Client not started');
		}
		const response = await this.client.updateMenu(body);
		console.log(response);
		if(response.data.status !== 200) {
			throw new Error(response.data.status + '');
		} else {
			MenuStore.addOrUpdateMenu(response.data.data.menu);
			return true;
		}
	}

	async deleteMenu(menuId: string) {
		if(this.client === null) {
			throw new Error('Client not started');
		}
		const response = await this.client.deleteMenu(menuId);
		console.log(response);
		if(response.data.status !== 200) {
			throw new Error(response.data.status + '');
		} else {
			MenuStore.removeMenu(menuId);
			return true;
		}
	}

	async fetchMenusWithItem(itemId: string) {
		const menuIds = MenuStore.menus.filter((menu) => {
			return itemId in menu.items;
		}).map((menu) => {
			return menu.menuId;
		});
		return Promise.all(menuIds.map((menuId) => {
			return this.fetchMenu(menuId);
		}));
	}

	async fetchMenusWithOption(optionId: string) {
		const menuIds = MenuStore.menus.filter((menu) => {
			return optionId in menu.options;
		}).map((menu) => {
			return menu.menuId;
		});
		return Promise.all(menuIds.map((menuId) => {
			return this.fetchMenu(menuId);
		}));
	}
}


export const WebService = new Web();
